import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";

export const inputCls =
  "w-full border border-charcoal bg-transparent px-3 py-2 text-sm font-light outline-none focus:ring-1 focus:ring-charcoal";

export const Field = ({ label, children, hint }) => (
  <div>
    <span className="eyebrow mb-2 block text-[0.65rem]">{label}</span>
    {children}
    {hint && <p className="mt-1.5 text-xs font-light opacity-60">{hint}</p>}
  </div>
);

export const StatusToggle = ({ value, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(value === "published" ? "draft" : "published")}
    className={`border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
      value === "published" ? "border-charcoal bg-charcoal text-offwhite" : "border-charcoal bg-transparent text-charcoal"
    }`}
  >
    {value === "published" ? "Published" : "Draft"}
  </button>
);

export const SaveButton = ({ busy, dirty, testid = "admin-save-button" }) => (
  <button
    type="submit"
    data-testid={testid}
    disabled={busy}
    className="border border-charcoal bg-charcoal px-6 py-2.5 font-condensed text-xs font-semibold uppercase tracking-[0.14em] text-offwhite transition-opacity duration-300 disabled:opacity-50"
  >
    {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
  </button>
);

export const ConfirmButton = ({ label, onConfirm, testid, className = "" }) => {
  const [confirming, setConfirming] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => {
        if (confirming) {
          setConfirming(false);
          onConfirm();
        } else {
          setConfirming(true);
          setTimeout(() => setConfirming(false), 4000);
        }
      }}
      className={`border border-charcoal px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
        confirming ? "bg-charcoal text-offwhite" : ""
      } ${className}`}
    >
      {confirming ? "Click again to confirm" : label}
    </button>
  );
};

export const useUnsavedWarning = (dirty) => {
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
};

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd);
  return data;
};

export const ImageField = ({ label, value, onChange, alt, onAltChange, focal, onFocalChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const res = await uploadFile(file);
      if (res.kind !== "image") throw new Error("Not an image");
      onChange(res.url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setFocal = (e) => {
    if (!onFocalChange) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 100);
    const y = Math.round(((e.clientY - r.top) / r.height) * 100);
    onFocalChange(`${x},${y}`);
  };

  const [fx, fy] = (focal || "50,50").split(",");

  return (
    <Field label={label} hint={onFocalChange ? "Click the preview to set the focal point for crops." : undefined}>
      <div
        data-testid={testid}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handle(e.dataTransfer.files?.[0]);
        }}
        className="border border-dashed border-charcoal p-4"
      >
        {value ? (
          <div className="relative cursor-crosshair" onClick={setFocal}>
            <img
              src={value}
              alt={alt || ""}
              className="max-h-56 w-auto object-contain"
              style={onFocalChange ? { objectPosition: `${fx}% ${fy}%` } : undefined}
            />
            {onFocalChange && (
              <span
                className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-charcoal bg-offwhite"
                style={{ left: `${fx}%`, top: `${fy}%` }}
                data-testid={`${testid}-focal`}
              />
            )}
          </div>
        ) : (
          <p className="py-6 text-center text-sm font-light opacity-60">Drag an image here, or</p>
        )}
        <div className="mt-3 flex items-center gap-4">
          <button
            type="button"
            data-testid={`${testid}-upload`}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="border border-charcoal px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em] disabled:opacity-50"
          >
            {busy ? "Uploading…" : value ? "Replace image" : "Choose image"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="link-arrow text-xs" data-testid={`${testid}-clear`}>
              Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
        {error && <p className="mt-2 text-xs" role="alert" data-testid={`${testid}-error`}>{error}</p>}
      </div>
      {onAltChange && (
        <input
          type="text"
          value={alt || ""}
          onChange={(e) => onAltChange(e.target.value)}
          placeholder="Alt text — describe the image"
          data-testid={`${testid}-alt`}
          className={`${inputCls} mt-3`}
        />
      )}
    </Field>
  );
};

export const VideoField = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError("That video is over 25MB. Please compress it and try again.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await uploadFile(file);
      onChange(res.url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label={label} hint="MP4, WebM or MOV, maximum 25MB.">
      <div
        data-testid={testid}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handle(e.dataTransfer.files?.[0]);
        }}
        className="border border-dashed border-charcoal p-4"
      >
        {value ? (
          <video src={value} muted playsInline controls className="max-h-48 w-auto" data-testid={`${testid}-preview`} />
        ) : (
          <p className="py-6 text-center text-sm font-light opacity-60">Drag a video here, or</p>
        )}
        <div className="mt-3 flex items-center gap-4">
          <button
            type="button"
            data-testid={`${testid}-upload`}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="border border-charcoal px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em] disabled:opacity-50"
          >
            {busy ? "Uploading…" : value ? "Replace video" : "Choose video"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="link-arrow text-xs" data-testid={`${testid}-clear`}>
              Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
        {error && <p className="mt-2 text-xs" role="alert" data-testid={`${testid}-error`}>{error}</p>}
      </div>
    </Field>
  );
};

export const SortableList = ({ items, onChange, onPersist, renderItem, keyFn, testid }) => {
  const from = useRef(null);
  return (
    <div data-testid={testid}>
      {items.map((item, i) => (
        <div
          key={keyFn(item)}
          draggable
          onDragStart={() => {
            from.current = i;
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (from.current === null || from.current === i) return;
            const next = [...items];
            const [moved] = next.splice(from.current, 1);
            next.splice(i, 0, moved);
            from.current = i;
            onChange(next);
          }}
          onDragEnd={() => {
            from.current = null;
            if (onPersist) onPersist();
          }}
          className="cursor-grab active:cursor-grabbing"
        >
          {renderItem(item, i)}
        </div>
      ))}
    </div>
  );
};

export const MetaLine = ({ by, at }) => {
  if (!at) return null;
  const when = new Date(at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  return (
    <p className="text-xs font-light opacity-60" data-testid="last-edited">
      Last edited {by ? `by ${by} ` : ""}on {when}
    </p>
  );
};
